/**
 * Tableau récapitulatif des marqueurs.
 *
 * Le même sur la vue d'ensemble et sur la section Marqueurs : la première le
 * rend en lecture seule et tronqué, la seconde avec les boutons de
 * modification et en entier. Deux tableaux jumeaux auraient divergé à la
 * première correction.
 *
 * La couleur ne porte jamais seule le verdict : une flèche donne le sens, un
 * libellé le nomme en mots pour les lecteurs d'écran, la couleur ne fait que
 * le rappeler.
 */

import type { ReactNode } from 'react';

import { IMPACT_METRICS, type MarkerRecapRow } from '../hooks/useMarkerRecap';
import { formatDayLong } from '../lib/format';
import { verdictGlyph } from '../lib/markers';
import { IMPACT_TONE_COLORS } from '../lib/theme';
import { MARKER_KIND_LABELS, MARKER_SCOPE_LABELS } from '@shared/types/stats';
import type { Marker } from '../types';

interface MarkerRecapTableProps {
  rows: readonly MarkerRecapRow[];
  /**
   * Nombre de lignes affichées, les plus récentes.
   *
   * Sert à la vue d'ensemble : trente marqueurs y noieraient les graphiques.
   * Omis, tout est affiché.
   */
  limit?: number;
  /** Boutons de fin de ligne. Absent = tableau en lecture seule. */
  renderActions?: (marker: Marker) => ReactNode;
  /** Rendu sous le tableau quand `limit` a tronqué la liste. */
  renderOverflow?: (hidden: number) => ReactNode;
  /** Atténue le tableau pendant qu'une nouvelle période charge. */
  isRefetching?: boolean;
}

export function MarkerRecapTable({
  rows,
  limit,
  renderActions,
  renderOverflow,
  isRefetching,
}: MarkerRecapTableProps) {
  const shown = limit === undefined ? rows : rows.slice(0, limit);
  const hidden = rows.length - shown.length;

  return (
    <>
      <div className={isRefetching ? 'table-wrap is-refetching' : 'table-wrap'}>
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th>Nature</th>
              <th>Affiché sur</th>
              <th>Libellé</th>
              {IMPACT_METRICS.map((metric) => (
                <th key={metric.key} className="num">
                  {metric.header}
                </th>
              ))}
              {renderActions && <th />}
            </tr>
          </thead>
          <tbody>
            {shown.map(({ marker, impacts, outOfPeriod }) => (
              <tr key={marker.id}>
                <td>
                  {formatDayLong(marker.date)}
                  {/* Dit pourquoi les quatre colonnes valent « — » : la
                      periode affichee ne couvre pas les alentours de ce
                      marqueur, il n'y a rien a comparer. */}
                  {outOfPeriod && <span className="marker-out">hors période</span>}
                </td>
                <td>{MARKER_KIND_LABELS[marker.kind]}</td>
                <td>{MARKER_SCOPE_LABELS[marker.scope ?? 'analytics']}</td>
                <td className="wrap">
                  {marker.label}
                  {marker.note && <span className="marker-note">{marker.note}</span>}
                </td>

                {/* Statistiques pas encore arrivees : autant de tirets que de
                    colonnes, pour que l'en-tete et le corps restent alignes. */}
                {impacts.length === 0 &&
                  IMPACT_METRICS.map((metric) => (
                    <td key={metric.key} className="num">
                      —
                    </td>
                  ))}

                {impacts.map(({ metric, impact, tone, delta, detail, verdict }) => (
                  <td key={metric.key} className="num" title={detail}>
                    <span style={{ color: IMPACT_TONE_COLORS[tone] }}>
                      <span aria-hidden="true">{verdictGlyph(impact.verdict)}</span> {delta}
                    </span>
                    <span className="sr-only"> — {verdict}</span>
                  </td>
                ))}

                {renderActions && <td>{renderActions(marker)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hidden > 0 && renderOverflow?.(hidden)}
    </>
  );
}
